/**
 * StoricoRichiestaModal — Timeline visuale degli eventi di una richiesta fornitore.
 * Mostra: creazione, invio, cambio stato, accettazione con rate, rate pagate, rifiuto.
 */
import React from "react";
import { X, Send, CheckCircle2, XCircle, FileText, CreditCard, Clock } from "lucide-react";

const eur = (n) => new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(n || 0);

function fmtDT(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}

const STATO_ICON = {
  bozza:     { Icon: FileText, color: "amber" },
  inviata:   { Icon: Send, color: "blue" },
  in_attesa: { Icon: Clock, color: "slate" },
  accettata: { Icon: CheckCircle2, color: "emerald" },
  rifiutata: { Icon: XCircle, color: "rose" },
  parziale:  { Icon: Clock, color: "amber" },
  ritirata:  { Icon: CheckCircle2, color: "emerald" },
  annullata: { Icon: XCircle, color: "slate" },
};

export default function StoricoRichiestaModal({ richiesta, onClose }) {
  const steps = React.useMemo(() => {
    const s = [];
    // Storico stati
    for (const h of (richiesta.storico || [])) {
      s.push({
        kind: "stato",
        stato: h.stato,
        when: h.changed_at,
        who: h.changed_by_email || h.changed_by || "sistema",
        note: h.note || "",
      });
    }
    // Rate accettate
    if (richiesta.stato === "accettata" && (richiesta.rate_pagamento || []).length > 0) {
      for (const r of richiesta.rate_pagamento) {
        if (r.stato === "pagato" && r.data_pagamento) {
          s.push({
            kind: "rata_pagata",
            when: r.data_pagamento,
            rata: r,
          });
        }
      }
    }
    // Ordina cronologicamente
    return s.sort((a, b) => (a.when || "").localeCompare(b.when || ""));
  }, [richiesta]);

  const rate = richiesta.rate_pagamento || [];
  const ratePagate = rate.filter((r) => r.stato === "pagato").length;
  const rateTotali = rate.length;

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="modal-storico-richiesta">
      <div className="bg-white rounded-md shadow-2xl w-full max-w-2xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="px-4 py-3 border-b border-slate-200 bg-slate-50 sticky top-0 flex items-start justify-between">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Storico richiesta</div>
            <h3 className="font-display font-bold text-lg text-slate-900">
              {richiesta.numero_richiesta} · {richiesta.fornitore_nome}
            </h3>
            <div className="text-xs text-slate-500 mt-0.5">
              {richiesta.cantiere_nome} · {eur(richiesta.totale_valore)}
              {rateTotali > 0 && (
                <span className="ml-2 inline-block px-2 py-0.5 rounded-sm text-[10px] font-bold uppercase tracking-widest bg-emerald-100 text-emerald-800 border border-emerald-300">
                  {ratePagate}/{rateTotali} rate pagate
                </span>
              )}
            </div>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-white rounded"><X className="w-5 h-5 text-slate-500" /></button>
        </div>

        <div className="p-6">
          {steps.length === 0 ? (
            <div className="p-8 text-center text-slate-500 text-sm italic">Nessun evento registrato.</div>
          ) : (
            <div className="relative border-l-2 border-slate-200 ml-4 space-y-6">
              {steps.map((st, i) => {
                if (st.kind === "rata_pagata") {
                  return (
                    <div key={i} className="relative pl-6" data-testid={`step-rata-${st.rata.numero}`}>
                      <div className="absolute -left-[10px] top-0 w-4 h-4 rounded-full bg-emerald-600 border-4 border-white ring-2 ring-emerald-200 flex items-center justify-center">
                        <CreditCard className="w-2 h-2 text-white" />
                      </div>
                      <div className="bg-emerald-50 border border-emerald-200 rounded-md p-3">
                        <div className="flex items-center justify-between gap-2 flex-wrap">
                          <div className="text-xs font-bold uppercase tracking-widest text-emerald-800">
                            💳 Rata {st.rata.numero}/{st.rata.totale_rate} pagata
                          </div>
                          <div className="text-[10px] font-mono text-slate-500">{fmtDT(st.when)}</div>
                        </div>
                        <div className="text-sm text-emerald-900 mt-1">
                          Importo pagato: <b>{eur(st.rata.importo_con_iva)}</b>
                          {" · "}<span className="text-slate-500">Scadenza era {st.rata.data_scadenza}</span>
                        </div>
                        {st.rata.note && <div className="text-[11px] text-slate-500 italic mt-1">{st.rata.note}</div>}
                      </div>
                    </div>
                  );
                }
                const cfg = STATO_ICON[st.stato] || STATO_ICON.bozza;
                const Icon = cfg.Icon;
                return (
                  <div key={i} className="relative pl-6" data-testid={`step-stato-${st.stato}`}>
                    <div className={`absolute -left-[10px] top-0 w-4 h-4 rounded-full bg-${cfg.color}-600 border-4 border-white ring-2 ring-${cfg.color}-200 flex items-center justify-center`}>
                      <Icon className="w-2 h-2 text-white" />
                    </div>
                    <div className={`bg-${cfg.color}-50 border border-${cfg.color}-200 rounded-md p-3`}>
                      <div className="flex items-center justify-between gap-2 flex-wrap">
                        <div className={`text-xs font-bold uppercase tracking-widest text-${cfg.color}-800`}>
                          {st.stato}
                        </div>
                        <div className="text-[10px] font-mono text-slate-500">{fmtDT(st.when)}</div>
                      </div>
                      <div className="text-xs text-slate-700 mt-0.5">
                        <span className="font-semibold">{st.who}</span>
                      </div>
                      {st.note && <div className="text-[11px] text-slate-700 italic mt-1 whitespace-pre-wrap">{st.note}</div>}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {rate.length > 0 && (
            <div className="mt-6 border-t border-slate-200 pt-4">
              <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-2">📅 Piano rate</div>
              <table className="w-full text-xs">
                <thead className="bg-slate-50 text-slate-600">
                  <tr>
                    <th className="text-left p-1.5">#</th>
                    <th className="text-left p-1.5">Scadenza</th>
                    <th className="text-right p-1.5">Importo</th>
                    <th className="text-right p-1.5">Totale IVA</th>
                    <th className="text-left p-1.5">Stato</th>
                    <th className="text-left p-1.5">Pagata</th>
                  </tr>
                </thead>
                <tbody>
                  {rate.map((r) => (
                    <tr key={r.numero} className="border-t border-slate-100">
                      <td className="p-1.5 font-mono">{r.numero}/{r.totale_rate}</td>
                      <td className="p-1.5 font-mono">{r.data_scadenza}</td>
                      <td className="p-1.5 text-right font-mono">{eur(r.importo)}</td>
                      <td className="p-1.5 text-right font-mono">{eur(r.importo_con_iva)}</td>
                      <td className="p-1.5">
                        <span className={`inline-block px-1.5 py-0.5 rounded-sm text-[10px] font-semibold ${r.stato === "pagato" ? "bg-emerald-100 text-emerald-800" : r.stato === "annullato" ? "bg-slate-100 text-slate-500" : "bg-amber-100 text-amber-800"}`}>
                          {r.stato}
                        </span>
                      </td>
                      <td className="p-1.5 font-mono text-slate-500">{r.data_pagamento || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
